package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.islamicmedia.shared.domain.model.DailyLimitNotice
import com.islamicmedia.shared.domain.model.LimitRequestCopy
import com.islamicmedia.shared.domain.model.LimitRequestUiState
import com.islamicmedia.shared.util.currentTimeMillis
import com.islamicmedia.site.theme.*
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.delay

/**
 * Shown when the account has used its fair-use allowance of analyses for the last 24 hours
 * (see [DailyLimitNotice]). Calm and informational, not an error. Praise, the limit, a warm close
 * (the copy's "sandwich"), then a live countdown in a pill with the clock time under it, and the
 * policy link sharing the button row. Text only, no icons. Mirrors composeApp/.../components/DailyLimitDialog.kt.
 *
 * Can appear with no analysis task open: the entitlement pre-flight raises it before a search
 * even starts, not just after a rejected /api/analyze call.
 *
 * "Request additional searches": when [canRequestMore] a secondary button under OK swaps this dialog's
 * content for a short form (see [LimitRequestForm]); Cancel swaps back, and Sent ends in a thank-you
 * with an OK that closes the dialog. The state ([requestState]) lives in the ViewModel; the text being
 * typed is held here, so an error keeps what was written. Not offered to library (search pack)
 * accounts, which have their own dialog.
 */
@Composable
fun DailyLimitDialog(
    notice: DailyLimitNotice,
    onDismiss: () -> Unit,
    canRequestMore: Boolean = false,
    requestState: LimitRequestUiState = LimitRequestUiState.Hidden,
    onRequestMore: () -> Unit = {},
    onSendRequest: (String) -> Unit = {},
    onCancelRequest: () -> Unit = {},
) {
    var requestText by remember { mutableStateOf("") }
    // A click outside the card normally closes the dialog. While the request form is open it only
    // closes the form (the text is kept), and while a request is on its way it does nothing, so a
    // stray click cannot throw away what someone took the time to write.
    val dismissRequest: () -> Unit = {
        when (requestState) {
            LimitRequestUiState.Hidden, LimitRequestUiState.Sent -> onDismiss()
            LimitRequestUiState.Sending -> Unit
            else -> onCancelRequest()
        }
    }
    // Live countdown. Re-reads the clock each tick rather than counting down from a start value, so
    // it stays right across a backgrounded tab (where timers are throttled). Wakes just after the
    // displayed second changes and stops at 0. Same loop as the mobile dialog.
    var nowMs by remember(notice) { mutableStateOf(currentTimeMillis()) }
    LaunchedEffect(notice) {
        while (true) {
            nowMs = currentTimeMillis()
            val left = notice.remainingMs(nowMs) ?: break
            if (notice.isWaitOver(nowMs)) break
            delay(left % 1000 + 20)
        }
    }

    Div(attrs = {
        onClick { dismissRequest() }
        style {
            property("position", "fixed")
            property("inset", "0")
            property("background", "rgba(0,0,0,0.45)")
            property("backdrop-filter", "blur(3px)")
            property("-webkit-backdrop-filter", "blur(3px)")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(16.px)
        }
    }) {
        Div(attrs = {
            onClick { it.stopPropagation() }
            style {
                backgroundColor(Color.white)
                borderRadius(24.px)
                padding(28.px, 24.px, 20.px)
                property("max-width", "380px")
                width(100.percent)
                property("box-sizing", "border-box")
                property("box-shadow", CardElevatedShadow)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                property("text-align", "center")
            }
        }) {
            if (requestState != LimitRequestUiState.Hidden) {
                LimitRequestForm(
                    state = requestState,
                    text = requestText,
                    onTextChange = { requestText = it.take(LimitRequestCopy.MAX_CHARS) },
                    onSend = { onSendRequest(requestText) },
                    onCancel = onCancelRequest,
                    onDone = onDismiss,
                )
            } else {
                P(attrs = {
                    style {
                        margin(0.px, 0.px, 10.px)
                        fontSize(20.px)
                        fontWeight(700)
                        property("font-family", TitleFontFamily)
                        property("line-height", "1.3")
                        color(Color("#171717"))
                    }
                }) { Text(notice.title) }

                P(attrs = {
                    style {
                        margin(0.px, 0.px, 10.px)
                        fontSize(15.px)
                        fontWeight(600)
                        color(Primary)
                        property("line-height", "1.4")
                    }
                }) { Text(notice.praise) }

                P(attrs = {
                    style {
                        margin(0.px, 0.px, 8.px)
                        fontSize(14.px)
                        color(Color("#525252"))
                        property("line-height", "1.5")
                    }
                }) { Text(notice.limitText) }

                P(attrs = {
                    style {
                        margin(0.px)
                        fontSize(14.px)
                        color(Color("#525252"))
                        property("line-height", "1.5")
                    }
                }) { Text(notice.closing) }

                // The wait: a live countdown, with the clock time it frees up underneath
                Div(attrs = {
                    style {
                        marginTop(16.px)
                        backgroundColor(PrimaryPale)
                        borderRadius(999.px)
                        padding(8.px, 14.px)
                        fontSize(13.px)
                        fontWeight(600)
                        color(Color("#065A5A"))
                        // Every digit the same width, so the pill doesn't wobble as it ticks.
                        property("font-variant-numeric", "tabular-nums")
                    }
                }) { Text(notice.countdownText(nowMs)) }
                notice.availableAtText(nowMs)?.let { at ->
                    P(attrs = {
                        style {
                            margin(6.px, 0.px, 0.px)
                            fontSize(12.px)
                            color(Color("#737373"))
                        }
                    }) { Text(at) }
                }

                // Policy link and OK on one row
                Div(attrs = {
                    style {
                        marginTop(24.px)
                        width(100.percent)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        property("gap", "12px")
                    }
                }) {
                    A(
                        href = DailyLimitNotice.FAIR_USE_PATH,
                        attrs = {
                            attr("target", "_blank")
                            attr("rel", "noopener noreferrer")
                            style {
                                fontSize(13.px)
                                color(Primary)
                                fontWeight(600)
                                property("text-decoration", "underline")
                            }
                        }
                    ) { Text(DailyLimitNotice.FAIR_USE_LINK_LABEL) }

                    Button(attrs = {
                        onClick { onDismiss() }
                        style {
                            backgroundColor(Primary)
                            color(Color.white)
                            property("border", "none")
                            cursor("pointer")
                            fontSize(15.px)
                            fontWeight(600)
                            padding(10.px, 32.px)
                            borderRadius(999.px)
                            property("box-shadow", "0 4px 12px rgba(14, 124, 123, 0.25)")
                        }
                    }) { Text("OK") }
                }

                if (canRequestMore) {
                    Button(attrs = {
                        onClick { onRequestMore() }
                        style {
                            marginTop(12.px)
                            width(100.percent)
                            backgroundColor(Color.white)
                            color(Primary)
                            property("border", "1px solid ${Primary}")
                            cursor("pointer")
                            fontSize(14.px)
                            fontWeight(600)
                            padding(10.px, 24.px)
                            borderRadius(999.px)
                            property("box-sizing", "border-box")
                        }
                    }) { Text(LimitRequestCopy.BUTTON) }
                }
            }
        }
    }
}

/**
 * The "Request additional searches" form, shown inside [DailyLimitDialog] in place of the notice.
 * Editing / Sending / Error all draw the same form (so the typed text and focus survive an error);
 * Sent replaces it with the thank-you. Send needs [LimitRequestCopy.MIN_CHARS] characters. Mirrors
 * the form in composeApp/.../components/DailyLimitDialog.kt.
 */
@Composable
private fun LimitRequestForm(
    state: LimitRequestUiState,
    text: String,
    onTextChange: (String) -> Unit,
    onSend: () -> Unit,
    onCancel: () -> Unit,
    onDone: () -> Unit,
) {
    if (state == LimitRequestUiState.Sent) {
        P(attrs = {
            style {
                margin(0.px)
                fontSize(16.px)
                fontWeight(600)
                color(Color("#171717"))
                property("line-height", "1.45")
            }
        }) { Text(LimitRequestCopy.SENT) }

        Button(attrs = {
            onClick { onDone() }
            style {
                marginTop(24.px)
                backgroundColor(Primary)
                color(Color.white)
                property("border", "none")
                cursor("pointer")
                fontSize(15.px)
                fontWeight(600)
                padding(10.px, 32.px)
                borderRadius(999.px)
                property("box-shadow", "0 4px 12px rgba(14, 124, 123, 0.25)")
            }
        }) { Text(LimitRequestCopy.OK) }
        return
    }

    val sending = state == LimitRequestUiState.Sending
    val canSend = !sending && LimitRequestCopy.canSend(text)

    P(attrs = {
        style {
            margin(0.px, 0.px, 10.px)
            fontSize(20.px)
            fontWeight(700)
            property("font-family", TitleFontFamily)
            property("line-height", "1.3")
            color(Color("#171717"))
        }
    }) { Text(LimitRequestCopy.TITLE) }

    P(attrs = {
        style {
            margin(0.px)
            fontSize(14.px)
            color(Color("#525252"))
            property("line-height", "1.5")
        }
    }) { Text(LimitRequestCopy.INTRO) }

    TextArea(value = text, attrs = {
        onInput { onTextChange(it.value) }
        placeholder(LimitRequestCopy.PLACEHOLDER)
        attr("rows", "5")
        attr("maxlength", LimitRequestCopy.MAX_CHARS.toString())
        if (sending) disabled()
        style {
            marginTop(16.px)
            width(100.percent)
            padding(10.px, 12.px)
            borderRadius(12.px)
            property("border", if (state is LimitRequestUiState.Error) "1.5px solid $ScoreRed" else "1px solid $Gray300")
            property("box-sizing", "border-box")
            property("font-family", "inherit")
            fontSize(14.px)
            property("line-height", "1.45")
            property("resize", "vertical")
            property("outline", "none")
            property("text-align", "left")
        }
    })

    if (state is LimitRequestUiState.Error) {
        P(attrs = {
            style {
                margin(8.px, 0.px, 0.px)
                fontSize(13.px)
                color(ScoreRed)
                property("line-height", "1.4")
            }
        }) { Text(state.message) }
    }

    Div(attrs = {
        style {
            marginTop(20.px)
            width(100.percent)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            property("gap", "12px")
        }
    }) {
        Button(attrs = {
            onClick { if (!sending) onCancel() }
            if (sending) disabled()
            style {
                backgroundColor(Color.transparent)
                property("border", "none")
                cursor(if (sending) "default" else "pointer")
                fontSize(15.px)
                fontWeight(600)
                color(if (sending) Gray400 else Gray600)
                padding(10.px, 16.px)
            }
        }) { Text(LimitRequestCopy.CANCEL) }

        Button(attrs = {
            onClick { if (canSend) onSend() }
            if (!canSend) disabled()
            style {
                backgroundColor(Primary)
                color(Color.white)
                property("border", "none")
                cursor(if (canSend) "pointer" else "default")
                fontSize(15.px)
                fontWeight(600)
                padding(10.px, 32.px)
                borderRadius(999.px)
                if (canSend) property("box-shadow", "0 4px 12px rgba(14, 124, 123, 0.25)")
                else property("opacity", "0.5")
            }
        }) { Text(if (sending) LimitRequestCopy.SENDING else LimitRequestCopy.SEND) }
    }
}
